import {
  Box,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Text,
  Code,
  Flex,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState, useCallback } from 'react';

import { error as commonError } from '@finley-technologies/common';

import {
  OmniEmbedModeGQLType,
  OmniConnectionRoleGQLType,
  OmniEntityFolderContentRoleGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useGenerateOmniEmbedUrlLazyQueryGQLType } from '../requests/omni.gql.generated';

type OmniEmbedContainerProps = {
  organizationId: string;
  mode: OmniEmbedModeGQLType;
  connectionRole?: OmniConnectionRoleGQLType;
  entityFolderContentRole?: OmniEntityFolderContentRoleGQLType;
  height?: string;
};

export const OmniEmbedContainer = ({
  organizationId,
  mode,
  connectionRole = 'RESTRICTED_QUERIER',
  entityFolderContentRole = 'editor',
  height = '800px',
}: OmniEmbedContainerProps) => {
  const toast = useToast();
  // Store the current embed URL to display and use
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [fetchEmbedUrl] = useGenerateOmniEmbedUrlLazyQueryGQLType({
    fetchPolicy: 'network-only',
  });

  // Fetch a fresh embed URL whenever organizationId or mode changes
  const loadEmbedUrl = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setEmbedUrl(null);

    try {
      const result = await fetchEmbedUrl({
        variables: {
          organizationId,
          embedParams: {
            mode,
            connectionRole,
            entityFolderContentRole,
          },
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message);
        Sentry.captureException(
          new commonError.FinleyError(
            `Failed to generate Omni embed URL [error:${result.error.message}]`,
            {
              metadata: {
                logMeta: {
                  error: result.error,
                  organizationId,
                  mode,
                },
              },
            },
          ),
        );
        toast({
          title:
            'An error occurred while loading Analytics V2. Please contact support@finleycms.com if this continues.',
          status: 'error',
          position: 'top',
          duration: 6000,
          isClosable: true,
        });
      } else if (result.data?.response.error) {
        setErrorMessage(result.data.response.error);
        Sentry.captureException(
          new commonError.FinleyError(
            `Omni embed URL generation error [error:${result.data.response.error}]`,
            { metadata: { logMeta: { organizationId, mode } } },
          ),
        );
      } else if (result.data?.response.url) {
        setEmbedUrl(result.data.response.url);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [
    organizationId,
    mode,
    connectionRole,
    entityFolderContentRole,
    fetchEmbedUrl,
    toast,
  ]);

  useEffect(() => {
    loadEmbedUrl();
  }, [loadEmbedUrl]);

  if (isLoading) {
    return (
      <Box>
        <Flex mb={2} fontSize="sm" color="gray.500" gap={2}>
          <Text>Loading Omni for entity:</Text>
          <Code>{organizationId}</Code>
        </Flex>
        <Skeleton height={height} data-testid="omni-embed-loading" />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          Failed to load Analytics V2: {errorMessage}
        </AlertDescription>
      </Alert>
    );
  }

  if (!embedUrl) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          No embed URL generated. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box id="omni-embed-container" w="100%">
      <Flex mb={2} fontSize="sm" color="gray.500" gap={2}>
        <Text>Entity (Organization ID):</Text>
        <Code>{organizationId}</Code>
      </Flex>
      <Box h={height}>
        <iframe
          // Key forces iframe to remount with fresh URL (one-time secrets can't be reused)
          key={embedUrl}
          src={embedUrl}
          title="Analytics V2"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '8px',
          }}
          allow="clipboard-write"
        />
      </Box>
    </Box>
  );
};
